import styled from '@emotion/styled';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import {
  ClickAction,
  ClickTargetType,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import {
  Direction,
  GAME_WEEKLY_NUMBERS,
  GameWeeklyNumber,
  getGameWeeklyNumberByItemKey,
  PromotionWeeklyKeywords,
  getLatestGameWeeklyNumber,
} from '@diritto/common/promotion/2025-balance-ritto.constants';
import { PromotionDto, PromotionItemDto } from '@diritto/common/promotion/dtos/promotion.dto';
import { usePromotionEligibilityCheck } from 'app-components/promotions/UsePromotionEligibilityCheck';
import { useGetPromotionParticipatesQuery } from 'queries/promotions/useGetPromotionParticipatesQuery';
import { useGetPromotionParticipatesByUserQuery } from 'queries/promotions/useGetPromotionParticipatesByUserQuery';
import { isPC, PCOffset } from 'constants/layout';
import { useAuthState } from 'contexts/AuthProvider';
import { useViewPort } from 'ui-hooks/useViewPort';
import { useTrackingState } from 'contexts/TrackingProvider';
import { configure } from 'utils/configure';
import { PromotionApi } from 'api/ApiHelper';
import { ModalHandler } from 'utils/ModalHandler';
import { ModalType } from 'constants/modal';
import { buildSignInUrl } from 'utils/url';
import { PromotionReplyListView } from 'app-components/promotions/PromotionReplyListView';
import { useGetPromotionItemQuery } from 'queries/promotions/useGetPromotionItemQuery';
import { useGetPromotionStatusQuery } from 'queries/promotions/useGetPromotionStatusQuery';
import { Loading } from 'ui-components/common/Loading';
import BaseTrackingService from '@diritto/common/tracker/BaseTrackingService';

const PROMOTION_ID = 4;

const REWARD_TOTAL_POINT_MAP = {
  0: '0P',
  1: '300P',
  2: '500P',
  3: '700P',
  4: '1,000P',
};

export const BalanceGameRitto2025 = () => {
  const { tracker } = useTrackingState();
  const { user } = useAuthState();
  const router = useRouter();
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const { data: promotionData } = useGetPromotionStatusQuery(PROMOTION_ID);
  const { data: promotionItemData } = useGetPromotionItemQuery(PROMOTION_ID, true);
  const promotionItems = useMemo(() => promotionItemData?.items ?? {}, [promotionItemData]);
  const activeWeeks = useMemo(
    () => Object.values(promotionItems ?? {}).map(item => getGameWeeklyNumberByItemKey(item.key)),
    [promotionItems],
  );

  useEffect(() => {
    const now = DateTime.local({ zone: 'Asia/Seoul' });
    if (promotionData && DateTime.fromISO(promotionData.startsAt) >= now) {
      router.back();
    }
  }, [promotionData, router]);

  useEffect(() => {
    const checkAllImagesLoaded = () => {
      const images = document.querySelectorAll('img');
      const loadedImages = Array.from(images).filter(img => img.complete);
      if (loadedImages.length === images.length) {
        setImagesLoaded(true);
      }
    };

    const images = document.querySelectorAll('img');
    images.forEach(img => {
      if (img.complete) {
        checkAllImagesLoaded();
      } else {
        img.addEventListener('load', checkAllImagesLoaded);
      }
    });

    return () => {
      images.forEach(img => {
        img.removeEventListener('load', checkAllImagesLoaded);
      });
    };
  }, []);

  useEffect(() => {
    if (promotionItems && Object.keys(promotionItems).length > 0) {
      const { hash } = window.location;
      if (hash === '#reply') {
        const replyElement = document.getElementById('reply');
        if (replyElement) {
          setTimeout(() => {
            replyElement.scrollIntoView();
          }, 300);
        }
      }
    }
  }, [promotionItems]);

  useEffect(() => {
    const handleHashScroll = () => {
      const { hash } = window.location;
      if (imagesLoaded && hash) {
        const hashedElement = document.getElementById(hash.replace('#', ''));
        if (hashedElement) {
          setTimeout(() => {
            hashedElement.scrollIntoView();
          }, 300);
        }
      }
    };

    handleHashScroll();
    window.addEventListener('hashchange', handleHashScroll);
    return () => {
      window.removeEventListener('hashchange', handleHashScroll);
    };
  }, [imagesLoaded]);

  const urls = [
    `${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/1_title.png`,
    `${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/2_detail.png`,
    `${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/3_event1.png`,
    `${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/4_event2.png`,
    `${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/5_ridi.png`,
    `${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/6_benefit1.png`,
    `${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/7_benefit2_2.png`,
    `${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/8_caution_2.png`,
  ];

  useEffect(() => {
    tracker.track(TrackingEventType.ViewPromotion, { promotionId: PROMOTION_ID, slug: '2025-balance-ritto' });
  }, [tracker]);

  let sections;
  if (user) {
    sections = ['participate', 'my', 'reward', 'reply'];
  } else {
    sections = ['participate', 'my', 'reward', 'reply'];
  }

  const [currentSection, setCurrentSection] = useState(sections[0]);

  const handleTabClick = useCallback(
    (section: string) => {
      tracker.track(TrackingEventType.Click, {
        label: section,
        action: ClickAction.EventButton,
        clickType: ClickTargetType.Menu,
        trackerExtraInfo: { trackerPosition: TrackerPosition.Promotion },
      });
      router.push(`#${section}`);
      setCurrentSection(section);
    },
    [router, tracker],
  );

  return (
    <div className="flex w-full h-full relative flex-col items-center gap-0 bg-[#f3f3f3]">
      <img key="title" className="w-full h-auto" alt="title" src={urls[0]} />
      <div id="participate" />
      <div className="sticky top-0 w-full z-[50]">
        <div className="relative scroll-mt-[70px] bg-[#000] w-full flex justify-center">
          <div className="flex justify-around items-center w-full">
            {sections.map(section => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
              <div
                key={section}
                className="flex flex-col relative items-center w-full h-full cursor-pointer py-[5%] text-[100%] pc:text-[150%]"
                onClick={() => handleTabClick(section)}>
                <div className="flex text-white font-semibold h-full  w-full text-center justify-center items-center leading-tight">
                  {section === 'participate' && <>참여 방법</>}
                  {section === 'my' && <>이럴 땐 나라면?!</>}
                  {section === 'reward' && <>리워드 안내</>}
                  {section === 'reply' && <>댓글</>}
                </div>
                {section === currentSection && <div className="absolute bottom-0 w-full h-[4%] bg-white" />}
              </div>
            ))}
          </div>
        </div>
      </div>
      <img key="detail" className="w-full h-auto" alt="detail" src={urls[1]} />
      <div id="my" className="scroll-mt-[70px]" />
      <BalanceGameRitto2025Event1 promotionItems={promotionItems} activeWeeks={activeWeeks} tracker={tracker} />
      <BalanceGameRitto2025Event2 />
      <BalanceGameRitto2025Ridi id="rewardCheck" />
      <div id="reward" className="scroll-mt-[70px]" />
      <img key="benefit1" className="w-full h-auto" alt="benefit1" src={urls[5]} />
      <img key="benefit2" className="w-full h-auto" alt="benefit2" src={urls[6]} />
      <img key="caution" className="w-full h-auto" alt="caution" src={urls[7]} />
      <ReplySection
        id="reply"
        promotion={promotionData}
        promotionItems={promotionItemData?.items}
        activeWeeks={activeWeeks}
      />
    </div>
  );
};

const selected = `${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/selected.png`;
const deselected = `${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/deselected.png`;

export const BalanceGameRitto2025Event1 = ({
  promotionItems,
  activeWeeks,
  tracker,
}: {
  promotionItems?: Record<string, PromotionItemDto>;
  activeWeeks: GameWeeklyNumber[];
  tracker: BaseTrackingService;
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const { width: screenWidth } = useViewPort();
  const { user, profile } = useAuthState();
  const height = 4321;
  const contentWidth = useMemo(
    () => (screenWidth > PCOffset ? Math.min(screenWidth, 730) : screenWidth),
    [screenWidth],
  );
  const scale = useMemo(() => contentWidth / 1110, [contentWidth]);
  const { isNotificationEnabled, isUserCertificated, ridiSsoLinked } = usePromotionEligibilityCheck();
  const { data: participateData, refetch: refetchParticipate } = useGetPromotionParticipatesQuery(PROMOTION_ID);
  const { data: participateByUserData, refetch: refetchParticipateByUser } = useGetPromotionParticipatesByUserQuery(
    PROMOTION_ID,
    !!user,
  );

  const imageUrls = useMemo(
    () => ({
      1: `${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/week1.png`,
      2: `${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/week2.png`,
      3: `${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/week3.png`,
      4: `${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/week4.png`,
    }),
    [],
  );
  const [currentWeek, setCurrentWeek] = useState<GameWeeklyNumber>(1);

  useEffect(() => {
    setCurrentWeek(getLatestGameWeeklyNumber(activeWeeks) ?? 1);
  }, [activeWeeks]);

  const selectedAnswers = useMemo(() => participateByUserData?.participateByWeek, [participateByUserData]);
  const latestProfileId = useMemo(() => participateByUserData?.profileId, [participateByUserData]);
  const isValidateProfileId = useMemo(() => profile?.id === latestProfileId, [profile, latestProfileId]);
  const getWeekAnswer = useCallback(
    (week: GameWeeklyNumber) => (isValidateProfileId ? selectedAnswers?.[week] : null),
    [selectedAnswers, isValidateProfileId],
  );
  const isActiveCurrentWeek = useMemo(() => {
    const now = DateTime.local({ zone: 'Asia/Seoul' });
    const promotionItem = Object.values(promotionItems ?? {}).find(
      item => item.key === `/promotion/2025-balance-ritto/${currentWeek}`,
    );

    if (!promotionItem) {
      return false;
    }

    return (
      DateTime.fromJSDate(new Date(promotionItem.startsAt)) <= now &&
      DateTime.fromJSDate(new Date(promotionItem.endsAt)) >= now
    );
  }, [currentWeek, promotionItems]);

  const currentRidiPoint = useMemo(
    () =>
      REWARD_TOTAL_POINT_MAP[
        Object.values(isValidateProfileId ? (selectedAnswers ?? {}) : {}).filter(answer => answer !== null)
          .length as keyof typeof REWARD_TOTAL_POINT_MAP
      ],
    [selectedAnswers, isValidateProfileId],
  );

  const selectedResult = useMemo(() => {
    const result = {} as Record<GameWeeklyNumber, { A: number; B: number }>;
    Object.entries(participateData?.itemCountsByWeek ?? {}).forEach(([week, stats]) => {
      result[week as unknown as GameWeeklyNumber] = {
        A: stats.A,
        B: stats.B,
      };
    });

    return result;
  }, [participateData]);

  const moreLinkList = useMemo(
    () => ({
      1: {
        A: `/search?q=${encodeURIComponent(PromotionWeeklyKeywords[1].A)}`,
        B: `/search?q=${encodeURIComponent(PromotionWeeklyKeywords[1].B)}`,
      },
      2: {
        A: `/search?q=${encodeURIComponent(PromotionWeeklyKeywords[2].A)}`,
        B: `/search?q=${encodeURIComponent(PromotionWeeklyKeywords[2].B)}`,
      },
      3: {
        A: `/search?q=${encodeURIComponent(PromotionWeeklyKeywords[3].A)}`,
        B: `/search?q=${encodeURIComponent(PromotionWeeklyKeywords[3].B)}`,
      },
      4: {
        A: `/search?q=${encodeURIComponent(PromotionWeeklyKeywords[4].A)}`,
        B: `/search?q=${encodeURIComponent(PromotionWeeklyKeywords[4].B)}`,
      },
    }),
    [],
  );

  const handleRoutePush = useCallback(
    (path: string) => {
      window.history.replaceState(null, '', window.location.pathname);
      router.push(path);
    },
    [router],
  );

  const handleMoreButtonClick = useCallback(
    (direction: Direction) => {
      tracker.track(TrackingEventType.Click, {
        label: PromotionWeeklyKeywords?.[currentWeek]?.[direction] ?? 'PromotionWeeklyKeywords',
        action: ClickAction.EventButton,
        clickType: ClickTargetType.MoreMenu,
        trackerExtraInfo: { trackerPosition: TrackerPosition.Promotion },
      });
      handleRoutePush(moreLinkList[currentWeek][direction]);
    },
    [tracker, currentWeek, moreLinkList, handleRoutePush],
  );

  const handleShare = useCallback(async () => {
    tracker.track(TrackingEventType.SharePromotion, {
      promotionId: PROMOTION_ID,
    });

    const image = await fetch(`${configure.staticPrefix}/images/og/balance-ritto.png?v=1`);
    const file = new File([await image.blob()], '2025-balance-game-ritto.png', { type: 'image/png' });
    navigator.share({
      title: '둘 중에 하나만 골라!',
      text: '👉선택하고 댓글 남기면 최대 11,000P #리토의밸런스게임 #디리토 #웹소설밸런스게임',
      url: 'https://www.diritto.co.kr/promotions/2025-balance-game-ritto',
      files: [file],
    });
  }, [tracker]);

  const handleParticipate = useCallback(
    async (direction: Direction) => {
      const promotionItemId = Object.values(promotionItems ?? {}).find(
        item => item.key === `/promotion/2025-balance-ritto/${currentWeek}`,
      )?.id;

      if (!promotionItemId) {
        alert('이벤트 참여 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      try {
        await PromotionApi.participate(promotionItemId, direction);
        await refetchParticipateByUser();
        await refetchParticipate();
      } catch (error) {
        alert('이벤트 참여 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      }
    },
    [currentWeek, refetchParticipateByUser, refetchParticipate, promotionItems],
  );

  const handleClosedQuestionModal = useCallback(() => {
    ModalHandler.show(ModalType.Alert, {
      title: '해당 질문은 마감되었어요.',
      confirmText: '닫기',
    });
  }, []);

  const handleAlreadyParticipatedModal = useCallback(() => {
    ModalHandler.show(ModalType.Alert, {
      title: '이미 선택한 주차입니다.',
      confirmText: '닫기',
    });
  }, []);

  const handleLoginModal = useCallback(() => {
    ModalHandler.show(ModalType.Alert, {
      title: '이벤트에 참여하시려면 먼저 로그인을 해주세요.',
      confirmText: '로그인',
      cancelText: '취소',
      onConfirm: () => handleRoutePush(buildSignInUrl(pathname ?? '')),
    });
  }, [handleRoutePush, pathname]);

  const handleEligibilityCheckModal = useCallback(() => {
    ModalHandler.show(ModalType.Alert, {
      title: '이벤트 참여 조건을 확인해주세요.',
      description: '이벤트에 참여하려면 필수 조건 3가지를 확인 후 완료해주세요.',
      confirmText: '조건 완료하러 가기',
      cancelText: '닫기',
      onConfirm: () => router.push('#rewardCheck'),
    });
  }, [router]);

  const handleValidateProfileIdModal = useCallback(() => {
    ModalHandler.show(ModalType.Alert, {
      title: '다른 프로필로 이벤트에 참여중이에요.',
      description: '이벤트 참여는 하나의 프로필로만 가능해요.',
      confirmText: '확인',
    });
  }, []);

  const handleParticipateConfirmModal = useCallback(
    (direction: Direction) => {
      ModalHandler.show(ModalType.Alert, {
        title: `${direction}를 선택하시겠어요?`,
        confirmText: '선택',
        cancelText: '취소',
        onConfirm: () => handleParticipate(direction),
      });
    },
    [handleParticipate],
  );

  const handleCheckParticipate = useCallback(
    (direction: Direction) => {
      if (!isActiveCurrentWeek) {
        handleClosedQuestionModal();
        return;
      }

      if (getWeekAnswer(currentWeek)) {
        handleAlreadyParticipatedModal();
        return;
      }

      if (!user) {
        handleLoginModal();
        return;
      }

      if (!isNotificationEnabled || !isUserCertificated || !ridiSsoLinked) {
        handleEligibilityCheckModal();
        return;
      }

      if (!isValidateProfileId) {
        handleValidateProfileIdModal();
        return;
      }

      handleParticipateConfirmModal(direction);
    },
    [
      getWeekAnswer,
      isActiveCurrentWeek,
      currentWeek,
      user,
      isNotificationEnabled,
      isUserCertificated,
      ridiSsoLinked,
      isValidateProfileId,
      handleLoginModal,
      handleParticipateConfirmModal,
      handleEligibilityCheckModal,
      handleClosedQuestionModal,
      handleAlreadyParticipatedModal,
      handleValidateProfileIdModal,
    ],
  );

  const renderQuestion = useCallback(
    (direction: Direction) => {
      const isClosedQuestion = !isActiveCurrentWeek;
      const weekAnswer = getWeekAnswer(currentWeek);
      const isSelected = weekAnswer === direction;
      const percentage = selectedResult?.[currentWeek]?.[direction] ?? 0;

      const styles = {
        container: 'w-[400px] h-[400px] relative cursor-pointer',
        percentage: 'text-[40px] font-bold absolute bottom-[30px] right-[30px]',
        selectedColor: 'text-[#E53935]',
        defaultColor: 'text-[#000]',
      };

      const renderContent = () => {
        if (weekAnswer) {
          return (
            <>
              <img className="w-full h-full" alt="empty" src={isSelected ? selected : deselected} />
              <p className={`${styles.percentage} ${isSelected ? styles.selectedColor : styles.defaultColor}`}>
                {percentage}%
              </p>
            </>
          );
        }

        if (isClosedQuestion) {
          return <p className={`${styles.percentage} ${styles.defaultColor}`}>{percentage}%</p>;
        }

        return null;
      };

      return (
        <div className={styles.container} role="button" onClick={() => handleCheckParticipate(direction)}>
          {renderContent()}
        </div>
      );
    },
    [currentWeek, getWeekAnswer, selectedResult, handleCheckParticipate, isActiveCurrentWeek],
  );

  return (
    <div
      className="flex flex-col relative w-full"
      style={{ width: `${contentWidth}px`, maxHeight: `${height * scale}px` }}>
      <StyledScaleWrapper className={`flex flex-1 w-[1110px] h-[${height}px]`} scale={scale}>
        <div className="flex w-full h-full relative flex-col items-center gap-0">
          <img
            key="event1"
            className="w-full h-auto"
            alt="event1"
            src={`${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/3_event1.png`}
          />
          <div className="flex w-[970px] top-[885px] absolute flex-col items-center gap-0">
            <img className="w-full h-auto" alt="question" src={imageUrls[currentWeek]} />
            {/* 주차별 탭 클릭영역 */}
            <div className="flex w-full h-full absolute top-0 flex-row gap-0">
              {GAME_WEEKLY_NUMBERS.map(week => {
                const isActiveWeek = activeWeeks.includes(week);
                return (
                  <div
                    key={week}
                    className={`w-[224px] h-[118px] ${isActiveWeek ? 'cursor-pointer' : ''}`}
                    role="button"
                    onClick={() => {
                      if (isActiveWeek) {
                        setCurrentWeek(week);
                      }
                    }}
                  />
                );
              })}
            </div>
            {/* 주차별 선택지 클릭영역 */}
            <div className="flex w-[830px] absolute left-[80px] top-[449px]  flex-row items-center justify-center gap-[30px]">
              {renderQuestion(Direction.A)}
              {renderQuestion(Direction.B)}
            </div>
            {/* 주차별 선택지 더 보기 클릭영역 */}
            <div className="flex w-[830px] absolute left-[80px] top-[889px]  flex-row items-center justify-center gap-[30px]">
              <div
                className="w-[400px] h-[88px] rounded-full cursor-pointer"
                role="button"
                onClick={() => {
                  // handleRoutePush(moreLinkList[currentWeek].A);
                  handleMoreButtonClick(Direction.A);
                }}
              />
              <div
                className="w-[400px] h-[88px] rounded-full cursor-pointer"
                role="button"
                onClick={() => {
                  // handleRoutePush(moreLinkList[currentWeek].B);
                  handleMoreButtonClick(Direction.B);
                }}
              />
            </div>
          </div>
          {/* 주차별 성공 스탬프 영역 */}
          <div className="flex w-[650px] h-[681px] absolute top-[2411px] left-[230px] items-center gap-0">
            {/* 1주차 */}
            {getWeekAnswer(1) && (
              <div className="top-[15px] left-0 w-[300px] h-[300px] absolute">
                <img
                  className="w-full h-full"
                  alt="stamp"
                  src={`${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/stamp_ok.png`}
                />
              </div>
            )}
            {/* 2주차 */}
            {getWeekAnswer(2) && (
              <div className="top-[15px] right-0 w-[300px] h-[300px] absolute">
                <img
                  className="w-full h-full"
                  alt="stamp"
                  src={`${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/stamp_ok.png`}
                />
              </div>
            )}
            {/* 3주차 */}
            {getWeekAnswer(3) && (
              <div className="bottom-[15px] left-0 w-[300px] h-[300px] absolute">
                <img
                  className="w-full h-full"
                  alt="stamp"
                  src={`${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/stamp_ok.png`}
                />
              </div>
            )}
            {/* 4주차 */}
            {getWeekAnswer(4) && (
              <div className="bottom-[15px] right-0 w-[300px] h-[300px] absolute">
                <img
                  className="w-full h-full"
                  alt="stamp"
                  src={`${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/stamp_ok.png`}
                />
              </div>
            )}
          </div>
          <div className="flex w-full top-[80%] absolute flex-col items-center gap-0">
            <p className="text-[#000] text-center justify-start text-[56px] font-bold leading-7">{currentRidiPoint}</p>
          </div>
          {/* 공유하기 버튼 클릭영역 */}
          <div
            className="flex w-[900px] h-[180px] top-[3940px] rounded-md absolute flex-col items-center gap-0 cursor-pointer"
            role="button"
            onClick={handleShare}
          />
        </div>
      </StyledScaleWrapper>
    </div>
  );
};

export const BalanceGameRitto2025Event2 = () => {
  const router = useRouter();
  const { width: screenWidth } = useViewPort();
  const height = 2729;
  const contentWidth = useMemo(
    () => (screenWidth > PCOffset ? Math.min(screenWidth, 730) : screenWidth),
    [screenWidth],
  );
  const scale = useMemo(() => contentWidth / 1110, [contentWidth]);
  const handleClick = useCallback(() => {
    router.push('#reply');
  }, [router]);

  return (
    <div
      className="flex flex-col relative w-full"
      style={{ width: `${contentWidth}px`, maxHeight: `${height * scale}px` }}>
      <StyledScaleWrapper className={`flex flex-1 w-[1110px] h-[${height}px]`} scale={scale}>
        <div className="flex w-full h-full relative flex-col items-center gap-0">
          <img
            key="event2"
            className="w-full h-auto"
            alt="event2"
            src={`${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/4_event2.png`}
          />
          <div
            className="flex w-[900px] h-[180px] top-[2090px] rounded-md absolute cursor-pointer"
            onClickCapture={handleClick}
          />
        </div>
      </StyledScaleWrapper>
    </div>
  );
};

export const BalanceGameRitto2025Ridi = ({ id }: { id: string }) => {
  const { width: screenWidth } = useViewPort();
  const height = 1489;
  const contentWidth = useMemo(
    () => (screenWidth > PCOffset ? Math.min(screenWidth, 730) : screenWidth),
    [screenWidth],
  );
  const scale = useMemo(() => contentWidth / 1110, [contentWidth]);

  const {
    handleMarketingAgreement,
    handleRidiSSoLink,
    handleUserCertification,
    isNotificationEnabled,
    isUserCertificated,
    ridiSsoLinked,
  } = usePromotionEligibilityCheck();

  const handlePromotionEligibilityRoute = useCallback(
    (type: 'userCertification' | 'marketingAgreement' | 'ridiSSoLink') => {
      window.history.replaceState(null, '', `${window.location.pathname}#${id}`);

      switch (type) {
        case 'userCertification':
          handleUserCertification();
          break;
        case 'marketingAgreement':
          handleMarketingAgreement();
          break;
        case 'ridiSSoLink':
          handleRidiSSoLink();
          break;
        default:
          break;
      }
    },
    [handleUserCertification, handleMarketingAgreement, handleRidiSSoLink, id],
  );

  return (
    <div
      id={id}
      className="flex flex-col relative w-full"
      style={{ width: `${contentWidth}px`, maxHeight: `${height * scale}px` }}>
      <StyledScaleWrapper className={`flex flex-1 w-[1110px] h-[${height}px]`} scale={scale}>
        <div className="flex w-full h-full relative flex-col items-center gap-0">
          <img
            key="ridi"
            className="w-full h-auto"
            alt="ridi"
            src={`${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/5_ridi.png`}
          />
          <div
            className="flex top-[720px] left-[217px] w-[161px] h-[228px] absolute flex-col items-center gap-0 cursor-pointer"
            onClick={() => {
              if (!isUserCertificated) {
                handlePromotionEligibilityRoute('userCertification');
              }
            }}>
            {isUserCertificated && (
              <img
                className="w-[161px] h-[161px]"
                alt="본인인증 완료"
                src={`${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/stamp_ok2.png`}
              />
            )}
          </div>
          <div
            className="flex top-[720px] left-[474px] w-[161px] h-[228px] absolute flex-col items-center gap-0 cursor-pointer"
            onClick={() => {
              if (!isNotificationEnabled) {
                handlePromotionEligibilityRoute('marketingAgreement');
              }
            }}>
            {isNotificationEnabled && (
              <img
                className="w-[161px] h-[161px]"
                alt="알림 동의 완료"
                src={`${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/stamp_ok2.png`}
              />
            )}
          </div>
          <div
            className="flex top-[720px] left-[731px] w-[161px] h-[228px] absolute flex-col items-center gap-0 cursor-pointer"
            onClick={() => {
              if (!ridiSsoLinked) {
                handlePromotionEligibilityRoute('ridiSSoLink');
              }
            }}>
            {ridiSsoLinked && (
              <img
                className="w-[161px] h-[161px]"
                alt="리디북스 연동 완료"
                src={`${configure.staticPrefix}/images/promotions/2025-balance-game-ritto/stamp_ok2.png`}
              />
            )}
          </div>
        </div>
      </StyledScaleWrapper>
    </div>
  );
};

const StyledScaleWrapper = styled.div<{ scale: number }>`
  transform: scale(${({ scale = 0.3 }) => scale});
  transform-origin: top left;
  position: relative;

  @media (${isPC}) {
    transform: scale(0.6576576577);
  }
`;

const ReplySection = ({
  id,
  promotion,
  promotionItems,
  activeWeeks,
}: {
  id: string;
  promotion?: PromotionDto;
  promotionItems?: Record<string, PromotionItemDto>;
  activeWeeks: GameWeeklyNumber[];
}) => (
  <div id={id} className="flex w-full h-full top-0 flex-col items-center scroll-mt-[70px] bg-white px-4 pt-[70px]">
    <div className="flex w-full h-full flex-col items-left">
      <div className="justify-start text-black text-lg font-bold leading-relaxed mb-6">이벤트 댓글</div>
    </div>
    {!promotionItems && <Loading />}
    {promotionItems && (
      <PromotionReplyListView
        promotion={promotion}
        promotionId={PROMOTION_ID}
        promotionItems={promotionItems}
        activeWeeks={activeWeeks}
      />
    )}
  </div>
);
